// apps/web/src/lib/agent-registry.ts
//
// LAT-622 / PRD §"Agent registry" + Locked decisions 14 & 17 — the data-driven
// registry of MCP clients the setup panel can connect. Components MUST read this
// registry rather than branching per-agent, so adding/retiring a client (or
// promoting a coming-soon one) is a single data edit here.
//
// The per-client `configTemplate` shapes are kept byte-for-byte in sync with the
// public docs (apps/site/public/agent-setup.html) so the app and the docs never
// drift. The canonical MCP endpoint is the one constant every prompt/config uses.

import type { BrandGlyphKey } from "@/lib/brand-keys";

export const MCP_ENDPOINT = "https://mcp.atmina.ai/mcp";
export const AGENT_SETUP_DOCS_URL = "https://atmina.ai/agent-setup";

export type AgentCategory = "web" | "cli" | "ide" | "generic";
// "internal" — a harness client recognized for activity legibility (brand glyph
// + friendly label) but deliberately absent from the onboarding picker, which
// reads SUPPORTED_AGENTS / COMING_SOON_AGENTS (both filter on status).
export type AgentStatus = "supported" | "coming_soon" | "internal";

export type AgentRegistryEntry = {
  agentKey: string;
  displayName: string;
  category: AgentCategory;
  /** Optional Brand Mark for client identity rendering. */
  brand?: BrandGlyphKey;
  status: AgentStatus;
  /**
   * Prod MCP `clientInfo.name` wire-names (verbatim) that resolve to THIS entry.
   * The client's self-reported id often differs from our slug — LAT-1229
   * follow-up: harness alias normalization. Resolution tries exact agentKey,
   * then exact alias, then a lowercase + trailing-parenthetical-stripped
   * normalization (see resolveActorIdentity).
   */
  aliases?: readonly string[];
  /**
   * Connection instructions for this client, shown above the contextual first
   * prompt. The PRD's contextual read-first/write-first prompt is generated
   * separately (see prompt-generator.ts) and appended for the copyable prompt.
   */
  setupPromptTemplate: string;
  /** Customer-facing destination where the copied setup prompt is pasted. */
  promptDestination: string;
  /**
   * The reliable, copyable config/command for this client (the SECONDARY copy
   * action). Omitted when there is no single reliable artifact (e.g. a UI-only
   * connector flow), in which case the panel hides the secondary action.
   */
  configTemplate?: string;
  docsUrl?: string;
  supportsOAuth: boolean;
  supportsSkills: boolean;
  supportsPlugins: boolean;
};

// MVP supported clients (PRD §"Agent registry"). Config shapes mirror
// apps/site/public/agent-setup.html per-client blocks verbatim.
export const AGENT_REGISTRY: readonly AgentRegistryEntry[] = [
  {
    agentKey: "claude-desktop",
    displayName: "Claude Desktop / web connectors",
    category: "web",
    brand: "claude",
    status: "supported",
    aliases: ["Anthropic/ClaudeAI"],
    setupPromptTemplate:
      "Add Atmina as a remote MCP connector in Claude Desktop or Claude on the " +
      "web: open Settings → Connectors → Add custom connector, paste the URL " +
      "below, and approve the OAuth flow in your browser. Then paste the first " +
      "prompt below.",
    promptDestination: "a new Claude chat",
    // UI-only connector flow — no single reliable copy-paste config. The panel
    // still surfaces the endpoint via the prompt; no secondary config action.
    docsUrl: AGENT_SETUP_DOCS_URL,
    supportsOAuth: true,
    supportsSkills: false,
    supportsPlugins: false
  },
  {
    agentKey: "claude-code",
    displayName: "Claude Code",
    category: "cli",
    brand: "claude",
    status: "supported",
    setupPromptTemplate:
      "Add Atmina as a remote MCP server from the command line, then paste the " +
      "first prompt below. The first tool call opens the OAuth flow in your " +
      "browser.",
    promptDestination: "the Claude Code prompt",
    configTemplate: `claude mcp add --transport http atmina ${MCP_ENDPOINT}`,
    docsUrl: AGENT_SETUP_DOCS_URL,
    supportsOAuth: true,
    supportsSkills: true,
    supportsPlugins: true
  },
  {
    agentKey: "codex",
    displayName: "Codex",
    category: "cli",
    brand: "openai",
    status: "supported",
    aliases: ["codex-mcp-client", "openai-mcp"],
    setupPromptTemplate:
      "Register Atmina as a remote MCP server and log in to complete OAuth. " +
      "Copy AGENTS.md and .agents/skills/atmina-kb-mcp/ from the atmina-plugins " +
      "Codex plugin into your repo, then paste the first prompt below.",
    promptDestination: "the Codex task input",
    configTemplate: `codex mcp add atmina --url ${MCP_ENDPOINT}\ncodex mcp login atmina`,
    docsUrl: AGENT_SETUP_DOCS_URL,
    supportsOAuth: true,
    supportsSkills: true,
    supportsPlugins: false
  },
  {
    agentKey: "cursor",
    displayName: "Cursor",
    category: "ide",
    brand: "cursor",
    status: "supported",
    aliases: ["cursor-vscode"],
    setupPromptTemplate:
      "Install the branded Atmina plugin from the atmina-plugins marketplace, " +
      "or add the remote MCP URL below to ~/.cursor/mcp.json (or the project's " +
      ".cursor/mcp.json), reload, then paste the first prompt below. Cursor " +
      "runs OAuth on first use.",
    promptDestination: "Cursor's Agent chat input",
    configTemplate: JSON.stringify(
      { mcpServers: { atmina: { url: MCP_ENDPOINT } } },
      null,
      2
    ),
    docsUrl: AGENT_SETUP_DOCS_URL,
    supportsOAuth: true,
    supportsSkills: false,
    supportsPlugins: true
  },
  {
    agentKey: "opencode",
    displayName: "OpenCode",
    category: "cli",
    status: "supported",
    setupPromptTemplate:
      "Add Atmina as a remote MCP server in your OpenCode config, authenticate, " +
      "then paste the first prompt below. OpenCode opens the OAuth flow in your " +
      "browser.",
    promptDestination: "the OpenCode session prompt",
    configTemplate: JSON.stringify(
      { mcp: { atmina: { type: "remote", url: MCP_ENDPOINT, enabled: true } } },
      null,
      2
    ),
    docsUrl: AGENT_SETUP_DOCS_URL,
    supportsOAuth: true,
    supportsSkills: false,
    supportsPlugins: false
  },
  {
    agentKey: "generic-mcp",
    displayName: "Generic MCP client",
    category: "generic",
    status: "supported",
    setupPromptTemplate:
      "Any client that speaks remote/streamable-HTTP MCP works. Point it at the " +
      "endpoint below and complete the OAuth authorization flow it surfaces, " +
      "then paste the first prompt below.",
    promptDestination: "the client's chat or agent input",
    configTemplate: `endpoint: ${MCP_ENDPOINT}\nauth:     OAuth 2.0 (authorization on connect/use)\nscope by: team-slug/kb-slug`,
    docsUrl: AGENT_SETUP_DOCS_URL,
    supportsOAuth: true,
    supportsSkills: false,
    supportsPlugins: false
  },
  // Coming soon / manual (Locked decision 14) — visibly non-actionable.
  {
    agentKey: "chatgpt",
    displayName: "ChatGPT",
    category: "web",
    // ChatGPT reuses the already-vendored OpenAI Brand Mark (LAT-1268).
    brand: "openai",
    status: "coming_soon",
    setupPromptTemplate: "",
    promptDestination: "",
    supportsOAuth: true,
    supportsSkills: false,
    supportsPlugins: false
  },
  {
    agentKey: "perplexity",
    displayName: "Perplexity",
    category: "web",
    status: "coming_soon",
    setupPromptTemplate: "",
    promptDestination: "",
    supportsOAuth: true,
    supportsSkills: false,
    supportsPlugins: false
  },
  {
    agentKey: "hermes",
    displayName: "Hermes",
    category: "generic",
    brand: "hermes",
    status: "coming_soon",
    aliases: ["hermes-agent"],
    setupPromptTemplate: "",
    promptDestination: "",
    supportsOAuth: true,
    supportsSkills: false,
    supportsPlugins: false
  },
  {
    agentKey: "openclaw",
    displayName: "OpenClaw",
    category: "generic",
    brand: "openclaw",
    status: "coming_soon",
    aliases: ["openclaw-bundle-mcp"],
    setupPromptTemplate: "",
    promptDestination: "",
    supportsOAuth: true,
    supportsSkills: false,
    supportsPlugins: false
  },
  // Internal / activity-only identities (LAT-1229 follow-up: harness alias
  // normalization). Recognized so audited activity reads with a brand glyph +
  // friendly label, but status "internal" keeps these out of
  // SUPPORTED/COMING_SOON — the onboarding picker never shows them.
  {
    agentKey: "pi",
    displayName: "Pi",
    category: "cli",
    brand: "pi",
    status: "internal",
    aliases: ["pi-coding-agent"],
    setupPromptTemplate: "",
    promptDestination: "",
    supportsOAuth: false,
    supportsSkills: false,
    supportsPlugins: false
  },
  {
    agentKey: "grok-shell",
    displayName: "Grok Shell",
    category: "cli",
    brand: "grok",
    status: "internal",
    aliases: ["grok-shell-atmina"],
    setupPromptTemplate: "",
    promptDestination: "",
    supportsOAuth: false,
    supportsSkills: false,
    supportsPlugins: false
  }
];

export const SUPPORTED_AGENTS = AGENT_REGISTRY.filter(
  (a) => a.status === "supported"
);
export const COMING_SOON_AGENTS = AGENT_REGISTRY.filter(
  (a) => a.status === "coming_soon"
);

export function getAgent(agentKey: string): AgentRegistryEntry | undefined {
  return AGENT_REGISTRY.find((a) => a.agentKey === agentKey);
}
